import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  XMarkIcon,
  PencilSquareIcon,
  TrashIcon,
  FolderIcon,
  FolderOpenIcon,
} from '@heroicons/react/24/outline';
import { kbApi } from '../services/kbApi';

interface TaxCategory {
  category: string;
  count: number;
  subcategories: { name: string; count: number }[];
}

/**
 * Admin modal to rename / delete KB categories and subcategories. Since these
 * are plain string fields on articles, every edit is a bulk update across all
 * articles carrying that category/subcategory — the affected count is shown.
 *
 * Deleting a category clears it (and its subcategories) on every article =
 * those articles become "Без категории". Deleting a subcategory just clears
 * the subcategory, articles stay under the parent category.
 */
const KBCategoryManager: React.FC<{ onClose: () => void; onChanged: () => void }>
  = ({ onClose, onChanged }) => {
  const [cats, setCats] = useState<TaxCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await kbApi.taxonomy();
      setCats(res.categories);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Ошибка загрузки категорий');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const renameCategory = async (cat: string) => {
    const next = window.prompt(`Переименовать категорию «${cat}» во всех статьях:`, cat);
    if (next === null) return;
    const newName = next.trim();
    if (!newName || newName === cat) return;
    await run(() => kbApi.editTaxonomy({ type: 'category', action: 'rename', name: cat, newName }));
  };

  const deleteCategory = async (cat: string) => {
    if (!window.confirm(`Удалить категорию «${cat}»? Статьи станут «Без категории» (сами статьи не удаляются).`)) return;
    await run(() => kbApi.editTaxonomy({ type: 'category', action: 'delete', name: cat }));
  };

  const renameSub = async (cat: string, sub: string) => {
    const next = window.prompt(`Переименовать подкатегорию «${sub}» (в категории «${cat}»):`, sub);
    if (next === null) return;
    const newName = next.trim();
    if (!newName || newName === sub) return;
    await run(() => kbApi.editTaxonomy({ type: 'subcategory', action: 'rename', category: cat, name: sub, newName }));
  };

  const deleteSub = async (cat: string, sub: string) => {
    if (!window.confirm(`Удалить подкатегорию «${sub}»? Статьи останутся в категории «${cat}».`)) return;
    await run(() => kbApi.editTaxonomy({ type: 'subcategory', action: 'delete', category: cat, name: sub }));
  };

  const run = async (fn: () => Promise<{ affected: number }>) => {
    setBusy(true);
    try {
      const res = await fn();
      toast.success(`Готово (затронуто статей: ${res.affected})`);
      await load();
      onChanged();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Ошибка');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-lg shadow-xl w-full max-w-lg mt-10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h2 className="text-base font-semibold text-foreground">Категории и подкатегории</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-accent text-muted-foreground">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Загрузка…</p>
          ) : cats.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              Пока нет категорий. Они появятся, когда вы зададите категорию в статьях.
            </p>
          ) : (
            <ul className="space-y-3">
              {cats.map((c) => (
                <li key={c.category} className="border border-border rounded-md">
                  <div className="flex items-center gap-2 px-3 py-2">
                    <FolderIcon className="h-4 w-4 text-amber-500 shrink-0" />
                    <span className="font-medium text-sm text-foreground truncate flex-1">
                      {c.category}
                    </span>
                    <span className="text-[10px] text-muted-foreground">{c.count}</span>
                    <button
                      disabled={busy}
                      onClick={() => renameCategory(c.category)}
                      className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground disabled:opacity-50"
                      title="Переименовать"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    <button
                      disabled={busy}
                      onClick={() => deleteCategory(c.category)}
                      className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive disabled:opacity-50"
                      title="Удалить категорию"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                  {c.subcategories.length > 0 && (
                    <ul className="border-t border-border/60 divide-y divide-border/40">
                      {c.subcategories.map((s) => (
                        <li key={s.name} className="flex items-center gap-2 pl-8 pr-3 py-1.5">
                          <FolderOpenIcon className="h-3.5 w-3.5 text-sky-500 shrink-0" />
                          <span className="text-xs text-foreground/90 truncate flex-1">{s.name}</span>
                          <span className="text-[10px] text-muted-foreground">{s.count}</span>
                          <button
                            disabled={busy}
                            onClick={() => renameSub(c.category, s.name)}
                            className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground disabled:opacity-50"
                            title="Переименовать подкатегорию"
                          >
                            <PencilSquareIcon className="h-3.5 w-3.5" />
                          </button>
                          <button
                            disabled={busy}
                            onClick={() => deleteSub(c.category, s.name)}
                            className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive disabled:opacity-50"
                            title="Удалить подкатегорию"
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default KBCategoryManager;
